// [FORK CLINICA] Regra pura do selo operacional da lista de conversas.
//
// O selo existe somente quando ha prova de que a proxima acao e da clinica:
// a ultima mensagem nao-atividade precisa ser publica, recebida do contato,
// acionavel e posterior ao ultimo marcador de resposta do WHU. Em duvida, o
// componente nao acusa atraso da funcionaria.

const PURE_ACKNOWLEDGEMENTS = new Set([
  'obg',
  'brigada',
  'brigado',
  'muito obrigada',
  'muito obrigado',
  'obrigada',
  'obrigada viu',
  'obrigado',
  'obrigado viu',
  'tudo bem',
]);

export const toEpochSeconds = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 10_000_000_000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (typeof value !== 'string' || value.trim() === '') return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
};

const normalizedText = value =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9?]+/g, ' ')
    .trim();

const isIncomingType = value => value === 0 || value === 'incoming';

const isContactSender = message => {
  const senderType = message?.sender_type ?? message?.sender?.type;
  if (senderType === 'Contact' || senderType === 'contact') return true;
  // [FORK CLINICA 31/08] INCOMING SEM sender_type TAMBEM E DO PACIENTE.
  // O servidor (ULTIMA_UTIL_SQL / TRATADA_SQL) so pergunta message_type = 0;
  // aqui exigia-se sender_type='Contact' e as mensagens que chegam sem esse
  // campo (9 em 7 dias, medido em 30/08) eram lidas como "nao e do paciente" —
  // mais uma fonte de o card e o contador discordarem. Mensagem RECEBIDA e do
  // contato por definicao; quando o campo falta, quem manda e o message_type.
  return (
    (senderType === undefined || senderType === null) &&
    isIncomingType(message?.message_type)
  );
};

export const isPublicPatientMessage = message =>
  Boolean(
    message &&
      isIncomingType(message.message_type) &&
      message.private === false &&
      isContactSender(message)
  );

export const isActionablePatientMessage = message => {
  if (!isPublicPatientMessage(message)) return false;
  if (Array.isArray(message.attachments) && message.attachments.length > 0) return true;

  const text = normalizedText(message.content);
  if (text === '') return true;
  return !PURE_ACKNOWLEDGEMENTS.has(text);
};

// [FORK CLINICA 26/08] GRUPO nao tem "paciente aguardando".
// O selo existe para dizer que a proxima acao e da clinica num atendimento de
// PACIENTE. Em grupo de parceiro qualquer mensagem de qualquer participante
// acende o relogio, e ele fica ligado para sempre — ensinando a equipe a
// ignorar o selo justamente onde ele importa. Mesmo motivo pelo qual o balde
// Grupos nasceu sem o balao verde.
// Identificamos pelo JID do WhatsApp (@g.us), que e dado de protocolo, e nao
// pelo sufixo "(Grupo)" do nome, que e texto editavel na tela de contatos.
const ehConversaDeGrupo = chat => {
  const ident =
    chat?.meta?.sender?.identifier ?? chat?.meta?.sender?.identifier ?? '';
  return String(ident).endsWith('@g.us');
};

// [FORK CLINICA 30/08] "MARCAR COMO TRATADA" — o segundo carimbo de que a
// clinica ja agiu.
//
// POR QUE EXISTE: a conversa foi resolvida por FORA (telefone, balcao, paciente
// ja agendado, disparo do sistema que ninguem precisa responder) e o card
// continuava com o sinal aceso. A unica forma de apagar era escrever no chat —
// mandar mensagem ao paciente so para limpar a tela. O botao do card
// (WhuCardActions -> POST whu_tratada) grava whu_tratada_em; aqui ele vale
// exatamente como valeria uma resposta nossa naquele instante.
//
// NAO E PERMANENTE DE PROPOSITO: e um CARIMBO DE TEMPO, nao uma bandeira.
// Mensagem nova depois dele reacende sozinha — se fosse booleano, a conversa
// marcada hoje ficaria muda para sempre e o paciente sumiria da fila.
// Espelhado no servidor por TRATADA_SQL (whu_stage_counts_controller).
const carimboDaClinica = chat =>
  Math.max(
    toEpochSeconds(chat?.custom_attributes?.whu_respondido_em),
    toEpochSeconds(chat?.custom_attributes?.whu_tratada_em)
  );

// [FORK CLINICA 31/08] "ACONTECEU ALGUMA COISA DEPOIS DO CARIMBO?"
//
// ERA "o PACIENTE falou depois?" — pergunta que o navegador NAO consegue
// responder. A sidebar recebe uma mensagem por conversa (json.messages =
// [ultima]) mais o last_non_activity_message; quando o paciente fala e logo
// depois qualquer outra coisa e escrita (o aviso de horario do bot, o disparo
// do sistema, a colega), a fala do paciente nao esta em fonte nenhuma
// carregada — e o waiting_since ja foi zerado pela outgoing, porque
// Message#clear_waiting_since_on_outgoing_response zera ate para o AgentBot
// (bot_response?). O carimbo entao NUNCA expirava aqui, enquanto o
// TRATADA_SQL do servidor — que varre messages — expirava: balao verde maior
// que a lista, a mesma queixa do waiting_since ja paga duas vezes.
//
// A pergunta nova e respondivel dos DOIS lados e da a mesma resposta:
// "existe mensagem nao-atividade depois do carimbo?". Aqui basta o
// last_non_activity_message, que e por definicao a nao-atividade MAIS RECENTE
// (scope non_activity_messages, reorder created_at desc); la e um NOT EXISTS
// com message_type <> 2. MEDIDO em 30/08 sobre as 2.5k conversas abertas,
// carimbo simulado em 1h/3h/12h/24h/72h: esta guarda divergiu ZERO vezes em
// todos os horizontes (a antiga divergia 1/4/6/7/22 no veredito final).
//
// O relogio entra como reforco, nao como regra: waiting_since so e armado por
// mensagem do paciente, entao waiting_since > carimbo e prova positiva mesmo
// se o store estiver com um last_non_activity_message velho (o cable atualiza
// a conversa, e nem todo evento traz o campo).
//
// ATENCAO: mudou aqui, muda o TRATADA_SQL do whu_stage_counts_controller.
const ehNaoAtividade = m => {
  const tipo = m?.message_type;
  return !!m && tipo !== 2 && tipo !== 'activity';
};

const algoAconteceuDepoisDe = (chat, lastMessage, quando) => {
  if (!quando) return false;
  if (toEpochSeconds(chat?.waiting_since) > quando) return true;
  const fontes = Array.isArray(chat?.messages) ? [...chat.messages] : [];
  fontes.push(lastMessage, chat?.last_non_activity_message);
  return fontes.some(
    m => ehNaoAtividade(m) && toEpochSeconds(m?.created_at) > quando
  );
};

export const deriveWhuStageWait = ({ chat, lastMessage }) => {
  if (ehConversaDeGrupo(chat)) {
    return { visible: false, start: 0, label: '', kind: 'none' };
  }

  const status = chat?.status;
  if (status === 'pending') {
    const start = toEpochSeconds(chat?.created_at);
    return { visible: start > 0, start, label: 'no bot há', kind: 'bot' };
  }

  if (status !== 'open' || !isActionablePatientMessage(lastMessage)) {
    return { visible: false, start: 0, label: '', kind: 'none' };
  }

  const patientAt = toEpochSeconds(lastMessage?.created_at);
  // [30/08] tratada conta como acao da clinica tambem: sem isto o relogio
  // "paciente aguarda ha 3h" continuaria correndo com a bolinha ja apagada —
  // card e selo dizendo coisas diferentes no MESMO card.
  const clinicAt = carimboDaClinica(chat);
  if (patientAt <= 0 || (clinicAt > 0 && patientAt <= clinicAt)) {
    return { visible: false, start: 0, label: '', kind: 'none' };
  }

  return {
    visible: true,
    start: patientAt,
    label: 'paciente aguarda há',
    kind: 'patient_waiting',
  };
};

// [FORK CLINICA 26/08] O PREDICADO UNICO de "a proxima acao e da clinica".
//
// POR QUE EXISTE: a bolinha verde do card era o unread_count NATIVO do
// Chatwoot, que zera quando a atendente ABRE a conversa. Quem entrava para ler
// e saia sem responder perdia o unico sinal visual que tinha, e a conversa
// sumia no fluxo. A equipe relatou esquecer de responder por causa disso.
// Agora o sinal so apaga quando alguem RESPONDE de fato.
//
// E o mesmo predicado que alimenta os filtros rapidos e o contador da sanfona.
// Isso e proposital: enquanto cada consumidor tinha a sua copia da regra, o
// numero do cabecalho e a lista discordavam, e a equipe parou de confiar no
// numero. Um predicado, tres consumidores, zero chance de divergir.
//
// DUAS FONTES, NESTA ORDEM:
//  1. PROVA DIRETA — se a ultima mensagem carregada existe, vale a mesma regra
//     do selo (deriveWhuStageWait), literalmente pelo mesmo caminho de codigo.
//     Isso garante que o card nunca mostre "paciente aguarda ha 10min" com a
//     bolinha apagada, nem o contrario.
//  2. RELOGIO — quando a lista ainda nao trouxe mensagem para aquela conversa,
//     cai no waiting_since, que e o equivalente client-side do NR_SQL do
//     servidor (inbox nativo basta; espelhado exige o relogio ser POSTERIOR ao
//     carimbo whu_respondido_em, porque resposta dada no WHU chega como nota
//     privada e nao zera o relogio daqui).
//
// waiting_since SOZINHO nao serve de prova: ele nem sempre zera quando a
// atendente responde daqui (conv 28174 gravou first_reply as 18:49 e deixou o
// relogio nas 11:30, no MESMO update). Por isso a prova direta vem primeiro.

// Canais NATIVOS do Chatwoot (sem espelho do WHU): neles o relogio e confiavel
// porque toda resposta passa por aqui.
export const INBOXES_NATIVOS = [1, 23];

// [FORK CLINICA 30/08] LOGINS QUE ESCREVEM SOZINHOS — nota deles NAO e nota
// de gente, e por isso nao apaga o sinal.
//   1  Nasser  — o login de TODA automacao (disparo, regua, ponte, oferta
//                Smart): 34.659 notas em 7 dias contra 197 do segundo colocado.
//   29 Copiloto IA — grava "sugestao da IA — NAO enviada ao paciente". Contar
//                isso como tratada apagaria o sinal justamente onde ninguem
//                falou com o paciente.
//   31 Claudia — a atendente-IA do Copiloto. A nota dela e o pedido de socorro
//                da escalada ("nao conseguiu responder apos 3 tentativas");
//                apagar o sinal ali seria enterrar a conversa que ela mesma
//                devolveu para um humano.
// Medido no banco (30/08), 7 dias de notas privadas por autor. Todos os demais
// autores sao pessoas — inclusive o Apoio 3 (id 17), cujas 197 notas sao o
// espelho "Enviado do WhatsApp" do inbox 29: mensagem que uma PESSOA mandou
// pelo aparelho, que e resposta de verdade.
// ATENCAO: esta lista tem espelho no ULTIMA_UTIL_SQL do
// whu_stage_counts_controller. Mudou aqui, muda la.
// [FORK CLINICA 25/09/2026] 33 Julia, 39 Tanaka e 47 Laura: atendentes-IA como a Claudia (mesmo motivo do 31).
// Espelho no ULTIMA_UTIL_SQL do whu_stage_counts_controller.
export const AUTORES_AUTOMATICOS = [1, 29, 31, 33, 39, 47];

// A fala do paciente e mais nova que o ultimo carimbo de resposta nossa?
// Sem isto, mensagem antiga do paciente que JA foi respondida (pelo WHU, que
// responde como nota privada e nao zera o relogio daqui) acenderia sinal.
const falaAindaNaoRespondida = (chat, mensagem) => {
  const doPaciente = toEpochSeconds(mensagem?.created_at);
  // [30/08] carimbo de tratada vale como resposta nossa (ver carimboDaClinica)
  const daClinica = carimboDaClinica(chat);
  if (doPaciente <= 0) return false;
  return !(daClinica > 0 && doPaciente <= daClinica);
};

// [FORK CLINICA 26/08] DOIS ESTADOS: QUEM FALOU POR ULTIMO.
//
//   'esperando'  — a ultima palavra e do paciente.
//   'respondida' — a ultima palavra e nossa.
//   'fora'       — nao entra na conta (encerrada, no bot, ou grupo).
//
// Houve por algumas horas um terceiro estado, 'encerrar', que separava o
// agradecimento puro ("obrigada") de uma pergunta de verdade. O Arthur
// removeu: "ou ta esperando resposta (ultima resposta do cliente) ou esta
// respondida (ultima resposta da nossa equipe)". Registro o que se ganhou e o
// que se perdeu, porque isso volta a ser discutido:
//   PERDEU-SE  a distincao entre "me responde" e "so falta encerrar" — e
//              encerrar e o que dispara o repasse ao Smart Card.
//   GANHOU-SE  alinhamento com o SERVIDOR: o NR_SQL do contador do balde
//              tambem nao distingue agradecimento (ele so pergunta se houve
//              mensagem nossa depois). Com tres estados, o chip e o balao
//              verde do cabecalho contavam coisas diferentes; com dois, nao.
//              E a conversa parada em "obrigada" continua VISIVEL, agora
//              dentro de "Esperando resposta" — nao se perde de vista, so nao
//              tem categoria propria.
//
// Um classificador so, e nao dois predicados soltos, porque os dois precisam
// ser exclusivos E exaustivos: conversa que nao cai em nenhum nao fica so de
// fora, DESAPARECE da tela — ja custou 1.048 conversas invisiveis aqui.
export const classificaResposta = (
  chat,
  lastMessage = null,
  nativos = INBOXES_NATIVOS
) => {
  if (!chat) return 'fora';
  // So conversa ABERTA. Resolvida nao espera nada, e pending esta com o robo:
  // ali a proxima acao nao e da clinica.
  // [FORK CLINICA 30/08] O balde Automatico passou a MOSTRAR as pending (elas
  // sumiam da sanfona inteira — ver casaEstagio no ChatList), mas elas seguem
  // 'fora' aqui de proposito: quem esta no meio do menu do bot nao esta
  // esperando um humano, e acender o balao verde nelas encheria o contador de
  // conversa que ninguem deve responder ainda. O card delas mostra o relogio
  // "no bot ha" (deriveWhuStageWait), que e a informacao certa. Consequencia
  // conhecida: nos chips de refino (Esperando/Respondidas) a pending nao entra
  // em nenhum dos dois — igual ao que ja acontece com grupo.
  if (chat.status !== 'open') return 'fora';
  // Grupo de parceiro: qualquer participante arma o relogio e o sinal ficaria
  // aceso para sempre, treinando a equipe a ignorar o sinal onde ele importa.
  if (ehConversaDeGrupo(chat)) return 'fora';

  // [FORK CLINICA 30/08] TRATADA MANUALMENTE: alguem clicou no botao do card
  // dizendo "isso ja foi resolvido, nao precisa responder". Vale para QUALQUER
  // motivo que acenderia o sinal (fala do paciente, transferencia recem
  // chegada, fila do setor, relogio) — por isso a guarda vem ANTES de todos
  // eles e nao dentro de um ramo so.
  // [FORK CLINICA 31/08] O carimbo vale ENQUANTO NADA DE NOVO ACONTECER na
  // conversa (ver algoAconteceuDepoisDe). Chegou mensagem depois dele, o
  // veredito volta a ser o do fluxo normal — que ja sabe o que fazer com cada
  // caso: o dono respondeu -> respondida; o disparo do sistema (user 1) ->
  // pendura no responsavel, decisao do Arthur em 28/08; bot ou colega falou ->
  // esperando. Espelho exato de TRATADA_SQL no servidor.
  const tratadaEm = toEpochSeconds(chat?.custom_attributes?.whu_tratada_em);
  if (tratadaEm > 0 && !algoAconteceuDepoisDe(chat, lastMessage, tratadaEm)) {
    return 'respondida';
  }

  // [FORK CLINICA 27/08] A PERGUNTA CERTA E: "a ultima palavra util e do DONO
  // ATUAL?" — e nao "a ultima e nossa?".
  //
  // O caso que derrubou a regra anterior (conv 39549): o bot transferiu para
  // a Typhanny e a ultima mensagem publica era DO BOT (instrucoes do
  // handoff). "Ultima e nossa" lia respondida, o sinal verde nao acendia, e a
  // equipe recebia transferencia como se nao fosse para responder. Ordem do
  // Arthur: transferencia — do bot, de colega, ou reabertura — chega PENDENTE
  // ate o novo dono falar.
  //
  // Como: percorre as mensagens de tras para frente IGNORANDO o bot
  // (AgentBot) e atividades. A ultima que sobra decide:
  //   - do paciente  -> esperando (regra literal de antes, mantida);
  //   - de um humano NOSSO: se e o DONO ATUAL -> respondida; se e OUTRA
  //     pessoa (quem transferiu, dono antigo) -> esperando — o novo dono
  //     ainda nao deu um alo.
  // Ignorar o bot resolve os dois lados: instrucao de handoff nao "responde"
  // pelo humano, e o aviso noturno de horario que o bot manda DEPOIS da
  // resposta da atendente nao reacende o sinal dela.
  // [FORK CLINICA 27/08] "Palavra util" em UM lugar so. Alem do publico
  // (paciente e User), a NOTA PRIVADA conta como tratada — pedido do Arthur
  // para a confirmacao: "confirmei por telefone" na nota apaga o sinal SEM
  // mandar mensagem ao cliente. Expira sozinha: o cliente falando depois da
  // nota volta a acender.
  // [FORK CLINICA 30/08] AGORA VALE A NOTA DE QUALQUER PESSOA, nao so a do
  // dono. Pedido do Arthur: "afrouxar e permitir q qualquer pessoa colocando
  // nota manualmente tire esse botao lateral, ja q padronizamos para q tudo q
  // e gerado pelo sistema seja ignorado". O criterio deixou de ser QUEM E O
  // DONO e passou a ser NOTA DE GENTE x NOTA DE MAQUINA — a lista de logins
  // automaticos e AUTORES_AUTOMATICOS (ver la em cima o porque de cada um).
  // O caso concreto que isso resolve: a colega registra "paciente ja foi
  // atendido no balcao" e o sinal some para o time inteiro, sem ninguem
  // precisar mandar mensagem ao paciente so para limpar a tela.
  // CUIDADO — isto vale SO PARA NOTA. Mensagem PUBLICA do user 1 (disparo do
  // sistema, que sai no nome da responsavel) continua contando como "nao foi
  // o dono que falou" e PENDURA a conversa nela: decisao explicita do Arthur
  // em 28/08 ("e pra ficar assim ate a Thamires colocar uma nota ou responder
  // ela mesma o cliente"). Um patch que mexia nisso foi revertido — nao
  // reabrir por aqui.
  const ehNotaDeGente = m => {
    const autorN = Number(m?.sender_id ?? m?.sender?.id ?? 0);
    return autorN > 0 && !AUTORES_AUTOMATICOS.includes(autorN);
  };
  const ehPalavraUtil = m => {
    if (!m) return false;
    const tipo = m.message_type;
    const st = m.sender_type ?? m.sender?.type;
    const deBot = st === 'AgentBot' || m.sender?.type === 'agent_bot';
    if (m.private) {
      return (
        (tipo === 1 || tipo === 'outgoing') && st === 'User' && ehNotaDeGente(m)
      );
    }
    if (tipo === 0 || tipo === 'incoming') return true;
    return (tipo === 1 || tipo === 'outgoing') && !deBot;
  };

  const listaMsgs = Array.isArray(chat.messages) ? chat.messages : [];
  let ultimaUtil = null;
  for (let i = listaMsgs.length - 1; i >= 0; i -= 1) {
    if (ehPalavraUtil(listaMsgs[i])) {
      ultimaUtil = listaMsgs[i];
      break;
    }
  }
  // lastMessage explicito (card) so vale se a lista nao trouxe nada melhor —
  // e SO se passar no MESMO criterio do loop acima. Sem este filtro o card
  // aceitava a mensagem do BOT como "ultima util" (a lista da sidebar entrega
  // last_non_activity_message, que inclui bot): a conv 39329 aparecia certa no
  // filtro "Esperando resposta" mas SEM a bolinha no card — lista e card
  // discordando dentro do proprio navegador (27/08).
  if (!ultimaUtil && ehPalavraUtil(lastMessage)) {
    ultimaUtil = lastMessage;
  }

  // [27/08 v2] Quando a ULTIMA entrada da conversa e um EVENTO (etiqueta,
  // atribuicao), o lote da lista traz so a atividade: o loop acima nao acha
  // nada e cairiamos no fallback "tem dono -> esperando" mesmo quando a dona
  // JA respondeu — o navegador dizia 51 e o servidor 47 (print da Anna
  // Clara). O payload da lista ja carrega last_non_activity_message; e a
  // mesma fonte que o servidor enxerga, entao usa-la aqui alinha os dois.
  // Mesmo criterio do resto: bot e atividade nao contam como palavra util.
  if (!ultimaUtil && ehPalavraUtil(chat.last_non_activity_message)) {
    ultimaUtil = chat.last_non_activity_message;
  }

  if (ultimaUtil) {
    if (isPublicPatientMessage(ultimaUtil)) {
      return falaAindaNaoRespondida(chat, ultimaUtil)
        ? 'esperando'
        : 'respondida';
    }
    // [FORK CLINICA 30/08] NOTA DE GENTE FECHA, VENHA DE QUEM VIER.
    // Sem esta saida a regra da transferencia logo abaixo ("a ultima palavra e
    // de outro usuario -> esperando") anularia o item de cima: a nota da
    // COLEGA — que e justamente o caso que o Arthur pediu — voltaria a acender
    // o sinal. So chega aqui nota que ja passou pelo ehPalavraUtil, ou seja,
    // nao e de login automatico.
    if (ultimaUtil.private) return 'respondida';
    const donoAtual = donoHumanoId(chat);
    const autor = ultimaUtil.sender_id ?? ultimaUtil.sender?.id ?? null;
    const autorEhUser =
      (ultimaUtil.sender_type ?? ultimaUtil.sender?.type) === 'User' ||
      ultimaUtil.sender?.type === 'user';
    if (autorEhUser && donoAtual && autor && Number(autor) !== Number(donoAtual)) {
      // transferida: quem falou por ultimo foi o dono ANTERIOR/colega.
      return 'esperando';
    }
    return 'respondida';
  }

  // [27/08] SEM fala util carregada (so bot/atividade) e COM dono: pendente.
  // E exatamente a cara de uma transferencia recem-chegada — a ultima coisa na
  // conversa e a instrucao do bot ou o evento de atribuicao. Melhor acender a
  // mais (o dono da um alo e apaga) do que entregar transferencia muda, que
  // foi o caso 39549. Espelha o fallback do NR_SQL no servidor.
  if (donoHumanoId(chat)) return 'esperando';

  // [FORK CLINICA 27/08c] FILA DO SETOR (aberta, SEM dono, COM time): quem
  // costuma zerar o relogio aqui e o AVISO AUTOMATICO do bot (horario,
  // handoff) — e aviso automatico NAO conta como resposta, mesma regra das
  // notas automaticas. Caso real: conv 40321 — paciente pergunta 21:14, o bot
  // responde o aviso de horario, o Chatwoot zera o waiting_since e o card
  // dizia "Respondida" DENTRO da fila (157 conversas assim nesta noite). O
  // servidor (ULTIMA_UTIL_SQL) ja contava como esperando; isto realinha o
  // card com o numero. Paciente em fila humana esta, por definicao,
  // esperando gente. Grupo nao chega aqui (ja saiu como 'fora').
  if (chat?.meta?.team?.id) return 'esperando';

  // SEM a mensagem carregada so resta o relogio. Cai em 'esperando', que e o
  // lado seguro: pior avisar demais do que deixar paciente sem resposta.
  const relogio = toEpochSeconds(chat.waiting_since);
  if (!relogio) return 'respondida';
  if (nativos.includes(Number(chat.inbox_id))) return 'esperando';
  const carimbo = toEpochSeconds(chat?.custom_attributes?.whu_respondido_em);
  return !carimbo || relogio > carimbo ? 'esperando' : 'respondida';
};

// Mantido como atalho porque o contador da sanfona e a bolinha do card so
// perguntam isto. Continua sendo a MESMA regra — nao ha segunda copia.
// [FORK CLINICA 27/08] O AGENT BOT SERIALIZADO NAO E DONO.
// Em inbox com bot ativo, o serializer devolve meta.assignee = o AgentBot
// ("Bot Clinica", id 1, com bot_type/outgoing_url) mesmo com assignee_id NULL
// no banco. O contador do servidor le a TABELA (sem dono -> Aguardando 2) e o
// navegador lia o PAYLOAD (dono id 1 -> Em atendimento): o balde abria vazio
// com contador cheio — caso da Anna Clara, convs 37921/37929. E a MESMA
// armadilha que ja mordeu a guarda v1 do motor ("meta.assignee devolve o
// AgentBot quando nao ha humano"), agora pela porta do front.
// Assinatura: User serializado tem email/role; AgentBot tem bot_type.
export const donoHumanoId = chat => {
  const a = chat?.meta?.assignee;
  if (!a) return null;
  if (a.bot_type || a.outgoing_url) return null;
  return a.id ?? null;
};

// [FORK CLINICA 21/09] DUVIDAS IA — a fila das ESCALADAS das atendentes-IA (Claudia 31, Julia 33, Tanaka 39).
// Ordem do dono (21/09/2026): "preciso q vc crie uma caixa... e todas as escaladas da ia vao para la... em cima de
// aguardando... e coloque Duvidas IA". A assinatura da escalada e a ETIQUETA `claudia-escalada`, que o BFF dos tres
// agentes aplica em TODA devolucao ao humano (escalada, agendamento sem confirmacao, troca pela metade, mudez).
// Enquanto nenhuma PESSOA assume (dono vazio ou o proprio login da IA), a conversa e desta fila; quando alguem assume,
// ehEscaladaIA vira falso e ela cai em "Em atendimento" pelo caminho de sempre. So conversa ABERTA: pending e do
// Automatico (o robo ainda conduz). ESPELHO EXATO no whu_stage_counts_controller (ESCALADA_IA_SQL) — mudou aqui, muda la.
export const ETIQUETA_ESCALADA_IA = 'claudia-escalada';
// [FORK CLINICA 02/10] VENDIDOS IA - grupo "Vendidos IA": conversas ABERTAS com a etiqueta `vendido-ia` (exame, orcamento ou pacote
// vendido pelas IAs/resolvedor/Priscila), de QUALQUER dono. E uma VISAO, nao um balde exclusivo: a conversa continua
// tambem no balde de sempre. ESPELHO no whu_stage_counts_controller (VENDIDO_IA_SQL) - mudou aqui, muda la.
export const ETIQUETA_VENDIDO_IA = 'vendido-ia';
export const ehVendidoIA = chat => {
  if (chat?.status !== 'open') return false;
  const etiquetas = Array.isArray(chat?.labels) ? chat.labels : [];
  return etiquetas.includes(ETIQUETA_VENDIDO_IA);
};
// [FORK CLINICA 25/09/2026] Laura 47 entra na caixa (ordem do dono, 24/09).
// [FORK CLINICA 19/10] Priscila 29 entra na caixa, e a escalada dela que vai para o login das automacoes (1) no time
// "ia resolvedor" tambem (ordem do Arthur, 19/10: as escaladas dela nunca apareciam aqui). O user 1 so conta NESSE time:
// fora dele e o Nasser em pessoa. Espelho EXATO no EscaladaIa (app/services/escalada_ia.rb) — mudou la, muda aqui.
export const USUARIOS_IA = [29, 31, 33, 39, 47];
const LOGIN_AUTOMACOES = 1;
const ehTimeResolvedor = chat =>
  String(chat?.meta?.team?.name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') === 'ia-resolvedor';
export const ehEscaladaIA = (chat, dono = undefined) => {
  if (chat?.status !== 'open') return false;
  const etiquetas = Array.isArray(chat?.labels) ? chat.labels : [];
  if (!etiquetas.includes(ETIQUETA_ESCALADA_IA)) return false;
  // [FORK CLINICA 19/10] vendido-ia fica só em Vendidos IA (dono: "faz os 2 ajustes"; Thamires: "vendas feitas pela IA ... está ficando nas dúvidas")
  if (etiquetas.includes(ETIQUETA_VENDIDO_IA)) return false;
  const d = dono === undefined ? donoHumanoId(chat) : dono;
  return (
    !d ||
    USUARIOS_IA.includes(d) ||
    (d === LOGIN_AUTOMACOES && ehTimeResolvedor(chat))
  );
};

export const estaEsperandoResposta = (chat, lastMessage = null, nativos = INBOXES_NATIVOS) =>
  classificaResposta(chat, lastMessage, nativos) === 'esperando';



// "Ja respondida" e o complemento EXATO dentro do universo que a tela mostra
// (aberta e nao-grupo). Definir como complemento, e nao com regra propria,
// garante que os dois filtros somem o total e que nenhuma conversa fique sem
// balde — o desaparecimento silencioso ja custou 1.048 conversas invisiveis
// aqui uma vez.
export const jaFoiRespondida = (chat, lastMessage = null) =>
  classificaResposta(chat, lastMessage) === 'respondida';
